import {
	ReactElement,
	forwardRef,
	Ref,
	useContext,
	TouchEventHandler,
	useMemo,
	useCallback,
	AriaAttributes,
	KeyboardEvent
} from "react";
import classNames from "classnames";
import {
	Icon,
	IconValue,
	FontIconName,
	SvgIconValue
} from "../Icon/Icon";
import styles from "./Button.module.less";
import { setTrackingId, EMPTY_ARRAY } from "client/Misc/utils";
import {
	ControlAttachmentContext,
	ControlAttachmentOptions,
	getAttachmentClassNames
} from "../ControlAttachment";
import { FormattedTextString } from "client/Misc/commonTypes";
import { FormattedText } from "../FormattedText/FormattedText";

export type ButtonKind =
	| "primary"
	| "secondary"
	| "transparent"
	| "transparent-for-list"
	| "gradient";

export type ButtonSize =
	| "normal"
	| "large";

export type ButtonHoverStyle =
	| "default"
	| "inversed";

const buttonKindToStyles: { [key in ButtonKind]: string | undefined } = {
	["primary"]: undefined,
	["secondary"]: styles.render_secondary,
	["transparent"]: styles.render_transparent,
	["transparent-for-list"]: styles.render_transparentForList,
	["gradient"]: styles.render_gradient
};

const buttonHoverStyleToStyles: { [key in ButtonHoverStyle]: string | undefined; } = {
	["default"]: styles.root_hoverStyleDefault,
	["inversed"]: styles.root_hoverStyleInversed
};

const buttonHoverStyleToTransparentStyles: { [key in ButtonHoverStyle]: string; } = {
	["default"]: styles.root_hoverStyleTransparent,
	["inversed"]: styles.root_hoverStyleTransparentInversed
};

export type PrimaryButtonColor =
	| "primary"
	| "header"
	| "app"
	| "info"
	| "success"
	| "warning"
	| "danger"
	| "bookmark";

export type ButtonClickEventArgs = {
	clientX: number;
	clientY: number;
};

export type ButtonProps = {
	size?: ButtonSize;
	type?: "button" | "submit";
	hoverStyle?: ButtonHoverStyle;
	onClick?(arg: ButtonClickEventArgs): void;
	bubbleClick?: boolean;
	onTouchStart?: TouchEventHandler;
	onTouchMove?: TouchEventHandler;
	onTouchEnd?: TouchEventHandler;
	isDisabled?: boolean;
	width?: "auto" | "fill";
	trackingId?: string;
	attachedOn?: ControlAttachmentOptions;
	tooltip?: string;
	isActive?: boolean;
	text: string | FormattedTextString;
	tabIndex?: number;
	onKeyDown?: (args: KeyboardEvent<HTMLElement>) => void;
	"aria-current"?: AriaAttributes["aria-current"];
}
	& (
		{ kind: Exclude<ButtonKind, "primary">; }
		|
		{ kind: "primary"; color?: PrimaryButtonColor; }
	)
	& (
		{
			kind: Exclude<ButtonKind, "transparent-for-list">;
			icon?: FontIconName | SvgIconValue;
			rightIcon?: FontIconName | SvgIconValue;
		}
		|
		{
			kind: "transparent-for-list";
			icon?: IconValue;
			rightIcon?: IconValue;
		}
	);

export const Button = forwardRef((props: ButtonProps, ref: Ref<HTMLButtonElement>): ReactElement => {
	const {
		type = "button",
		attachedOn = EMPTY_ARRAY,
		kind,
		hoverStyle = "default",
		onClick,
		bubbleClick,
		onTouchMove,
		onTouchStart,
		onTouchEnd,
		isDisabled,
		icon,
		rightIcon,
		text,
		width = "auto",
		trackingId,
		tooltip,
		isActive = false,
		size = "normal",
		tabIndex,
		onKeyDown
	} = props;

	const color: PrimaryButtonColor | undefined = props.kind === "primary"
		? props.color ?? "primary"
		: undefined;

	const isTransparentKind: boolean = kind === "transparent"
		|| kind === "transparent-for-list";

	const isPrimaryIcon: boolean = kind === "transparent"
		|| kind === "transparent-for-list";

	const {
		attachedOn: attachmentsFromContext
	} = useContext(ControlAttachmentContext);

	const renderText = (): ReactElement | string | undefined => {
		if (typeof text === "string" || typeof text === "undefined") {
			return text;
		}

		return (
			<FormattedText
				text={text.template}
				thingsToReplace={text.replacements}
			/>
		);
	};

	const renderedText: ReactElement | string | undefined = renderText();

	const iconValue: IconValue | undefined = useIconValue(
		icon,
		isDisabled,
		isPrimaryIcon
	);

	const rightIconValue: IconValue | undefined = useIconValue(
		rightIcon,
		isDisabled,
		isPrimaryIcon
	);

	const clickHandler = useCallback(
		(args: React.MouseEvent) => {
			const {
				clientX,
				clientY
			} = args;

			if (bubbleClick === false) {
				args.preventDefault();
				args.stopPropagation();
			}

			if (onClick) {
				onClick({ clientX, clientY });
			}
		},
		[bubbleClick, onClick]
	);

	return (
		<button
			{...setTrackingId(trackingId)}
			type={type}
			className={classNames(
				styles.root,
				{
					[styles.root_fill]: width === "fill",
					[styles.root_active]: isActive,
					[styles.root_large]: size === "large"
				},
				isTransparentKind
					? buttonHoverStyleToTransparentStyles[hoverStyle]
					: buttonHoverStyleToStyles[hoverStyle],
				getAttachmentClassNames(attachedOn, attachmentsFromContext)
			)}
			onClick={clickHandler}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			disabled={isDisabled}
			ref={ref}
			title={tooltip}
			aria-current={props["aria-current"]}
			tabIndex={tabIndex}
			onKeyDown={onKeyDown}
		>
			<span
				className={classNames(
					styles.render,
					buttonKindToStyles[kind],
					color && colorToStyleMap[color],
					getAttachmentClassNames(attachedOn, attachmentsFromContext)
				)}
			>
				{iconValue && (
					<span className={styles.icon}>
						<Icon value={iconValue} />
					</span>
				)}
				{ renderedText && <span className={styles.text}>{renderedText}</span> }
				{rightIconValue && (
					<span className={styles.rightIcon}>
						<Icon value={rightIconValue} />
					</span>
				)}
			</span>
		</button>
	);
});

Button.displayName = "Button";

const colorToStyleMap: Record<PrimaryButtonColor, string> = {
	["primary"]: styles.render_primary,
	["header"]: styles.render_header,
	["app"]: styles.render_app,
	["info"]: styles.render_info,
	["success"]: styles.render_success,
	["warning"]: styles.render_warning,
	["danger"]: styles.render_danger,
	["bookmark"]: styles.render_bookmark
};

function useIconValue(
	icon: IconValue | undefined,
	isDisabled: boolean | undefined,
	isPrimaryColor: boolean
): IconValue | undefined {
		const iconValue = useMemo(
			(): IconValue | undefined => {
				if(typeof icon === "string") {
					return {
						kind: "font",
						name: icon,
						color: isDisabled
							? undefined
							: isPrimaryColor
								? "primary"
								: undefined
					};
				}

				if (isDisabled
					&& icon
					&& icon.kind === "font"
				) {
					return {
						...icon,
						color: undefined
					};
				}

				return icon;
			},
			[
				icon,
				isDisabled,
				isPrimaryColor
			]
		);

		return iconValue;
};